// src/components/admin/AdminOverviewTab.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ArticleIcon from '@mui/icons-material/Article';
import CommentIcon from '@mui/icons-material/Comment';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EventIcon from '@mui/icons-material/Event';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import VerifiedIcon from '@mui/icons-material/Verified';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAdminOverview } from '../../services/adminService';
import { AdminOverviewResponse } from '../../types/admin';

const AdminOverviewTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAdminOverview();
      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch admin overview:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Parse growth rate to determine trend
  const getGrowthTrend = (rate: string) => {
    const value = parseFloat(rate.replace('%', '').replace('+', ''));
    if (value > 0) return { icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, color: '#10b981', label: rate };
    if (value < 0) return { icon: <TrendingDownIcon sx={{ fontSize: 16 }} />, color: '#ef4444', label: rate };
    return { icon: <TrendingFlatIcon sx={{ fontSize: 16 }} />, color: '#64748b', label: '0%' };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const growthTrend = getGrowthTrend(data.users.growth_rate_7d);

  return (
    <Box sx={{ p: 3 }}>
      {/* Alerts Section */}
      {data.alerts_count > 0 && (
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: data.alerts.some(a => a.type === 'error') ? '#fecaca' : '#fef3c7',
            bgcolor: data.alerts.some(a => a.type === 'error') ? '#fef2f2' : '#fffbeb',
          }}
        >
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              {data.alerts.map((alert, index) => (
                <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
                  {alert.type === 'error' ? (
                    <ErrorIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                  ) : alert.type === 'warning' ? (
                    <WarningIcon sx={{ fontSize: 18, color: '#d97706' }} />
                  ) : (
                    <InfoIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {alert.message}
                  </Typography>
                  {alert.action_url && (
                    <Tooltip title={t('common.view')}>
                      <IconButton size="small" onClick={() => navigate(alert.action_url)}>
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2.5}>
        {/* Growth & Trends - Left Column */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2.5}>
            {/* User Growth Card */}
            <Grid item xs={12} sm={6}>
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                      {t('adminOverview.userGrowth')}
                    </Typography>
                    <Chip
                      icon={growthTrend.icon}
                      label={growthTrend.label}
                      size="small"
                      sx={{
                        bgcolor: `${growthTrend.color}15`,
                        color: growthTrend.color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        '& .MuiChip-icon': { color: growthTrend.color },
                      }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography variant="h4" fontWeight={700} color="#1e293b">
                        +{data.users.new_7d}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('adminOverview.last7Days')}
                      </Typography>
                    </Box>
                    <Box sx={{ borderLeft: '1px solid #e2e8f0', pl: 3 }}>
                      <Typography variant="h4" fontWeight={700} color="#1e293b">
                        +{data.users.new_30d}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('adminOverview.last30Days')}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Engagement Rate Card */}
            <Grid item xs={12} sm={6}>
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                    {t('adminOverview.engagement')}
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography variant="h4" fontWeight={700} color="#1e293b">
                          {data.engagement.engagement_rate}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('adminOverview.engagementRate')}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(data.engagement.engagement_rate) * 10, 100)}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: '#e2e8f0',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            bgcolor: '#2563eb',
                          },
                        }}
                      />
                    </Box>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        <strong>{data.engagement.active_users_7d.toLocaleString()}</strong> {t('adminOverview.activeUsers7d')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>{data.engagement.avg_posts_per_day.toFixed(1)}</strong> {t('adminOverview.postsPerDay')}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Content Activity Row */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                    {t('adminOverview.contentActivity')}
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Total Users */}
                    <Grid item xs={6} sm={3}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 40, height: 40, bgcolor: '#dbeafe' }}>
                          <GroupsIcon sx={{ fontSize: 20, color: '#2563eb' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700} color="#1e293b">
                            {data.summary.total_users.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('adminOverview.totalUsers')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Pending Verifications */}
                    <Grid item xs={6} sm={3}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 40, height: 40, bgcolor: '#fef3c7' }}>
                          <VerifiedIcon sx={{ fontSize: 20, color: '#d97706' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700} color="#1e293b">
                            {data.summary.pending_verifications}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('adminOverview.pendingVerifications')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Pending Reports */}
                    <Grid item xs={6} sm={3}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 40, height: 40, bgcolor: '#fce7f3' }}>
                          <FeedbackIcon sx={{ fontSize: 20, color: '#db2777' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700} color="#1e293b">
                            {data.summary.pending_reports}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('adminOverview.pendingReports')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Blocked Users */}
                    <Grid item xs={6} sm={3}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 40, height: 40, bgcolor: '#fee2e2' }}>
                          <ErrorIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700} color="#1e293b">
                            {data.summary.blocked_users}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('adminOverview.blockedUsers')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* User Verification & Roles */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                    {t('adminOverview.userBreakdown')}
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Verification Status */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <VerifiedIcon sx={{ fontSize: 16, color: '#10b981' }} />
                            <Typography variant="body2">{t('adminOverview.verified')}</Typography>
                          </Stack>
                          <Typography variant="body2" fontWeight={600}>{data.users.verified.toLocaleString()}</Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={(data.users.verified / data.users.total) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#10b981' },
                          }}
                        />
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            {((data.users.verified / data.users.total) * 100).toFixed(1)}% {t('adminOverview.verificationRate')}
                          </Typography>
                          <Typography variant="caption" color="#f59e0b">
                            {data.users.unverified} {t('adminOverview.unverified')}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Grid>

                    {/* Role Distribution */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={2} justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <GroupsIcon sx={{ fontSize: 16, color: '#64748b' }} />
                            <Typography variant="body2">{t('adminOverview.users')}</Typography>
                            <Typography variant="body2" fontWeight={600}>{data.users.by_role.user}</Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AdminPanelSettingsIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                            <Typography variant="body2">{t('adminOverview.admins')}</Typography>
                            <Typography variant="body2" fontWeight={600}>{data.users.by_role.admin}</Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <SupervisorAccountIcon sx={{ fontSize: 16, color: '#d97706' }} />
                            <Typography variant="body2">{t('adminOverview.superAdmins')}</Typography>
                            <Typography variant="body2" fontWeight={600}>{data.users.by_role.super_admin}</Typography>
                          </Stack>
                        </Stack>
                        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                          <Box
                            sx={{
                              width: `${(data.users.by_role.user / data.users.total) * 100}%`,
                              bgcolor: '#94a3b8',
                            }}
                          />
                          <Box
                            sx={{
                              width: `${(data.users.by_role.admin / data.users.total) * 100}%`,
                              bgcolor: '#2563eb',
                            }}
                          />
                          <Box
                            sx={{
                              width: `${(data.users.by_role.super_admin / data.users.total) * 100}%`,
                              bgcolor: '#d97706',
                            }}
                          />
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column - Quick Stats */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2.5}>
            {/* Platform Stats */}
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                  Statistiques Plateforme
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Utilisateurs actifs</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                      <Typography variant="body2" fontWeight={600}>
                        {data.users.active}/{data.users.total}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Profils vérifiés</Typography>
                    <Typography variant="body2" fontWeight={600}>{data.users.verified.toLocaleString()}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Utilisateurs bannis</Typography>
                    <Typography variant="body2" fontWeight={600} color="#ef4444">{data.users.banned}</Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Feedbacks */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: data.feedbacks.pending > 0 ? '#fef3c7' : '#e2e8f0',
                bgcolor: data.feedbacks.pending > 0 ? '#fffbeb' : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#2563eb' },
              }}
              onClick={() => navigate('/superadmin?tab=feedbacks')}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FeedbackIcon sx={{ fontSize: 18, color: data.feedbacks.pending > 0 ? '#d97706' : '#64748b' }} />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                      {t('adminOverview.feedbacks')}
                    </Typography>
                  </Stack>
                  {data.feedbacks.pending > 0 && (
                    <Chip
                      label={`${data.feedbacks.pending} ${t('adminOverview.pending')}`}
                      size="small"
                      sx={{
                        bgcolor: '#fef3c7',
                        color: '#d97706',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#1e293b">{data.feedbacks.total}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('adminOverview.total')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#10b981">{data.feedbacks.resolved}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('adminOverview.resolved')}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Moderation Queue */}
            {data.content.posts.pending_moderation > 0 && (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid #fecaca',
                  bgcolor: '#fef2f2',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#dc2626' },
                }}
                onClick={() => navigate('/superadmin?tab=moderation')}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                      <Typography variant="body2" fontWeight={500} color="#dc2626">
                        {t('adminOverview.pendingModeration')}
                      </Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={700} color="#dc2626">
                      {data.content.posts.pending_moderation}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Quick KPIs */}
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                  {t('adminOverview.quickMetrics')}
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('adminOverview.avgCommentsPerPost')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {data.engagement.avg_comments_per_post.toFixed(2)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('adminOverview.reposts')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {data.content.reposts.total.toLocaleString()}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('adminOverview.newToday')}
                    </Typography>
                    <Chip
                      icon={<PersonAddIcon sx={{ fontSize: 14 }} />}
                      label={`+${data.users.new_today}`}
                      size="small"
                      sx={{
                        bgcolor: data.users.new_today > 0 ? '#dcfce7' : '#f1f5f9',
                        color: data.users.new_today > 0 ? '#16a34a' : '#64748b',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 22,
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminOverviewTab;
